/* The dashboard - DSH-01..11.
 *
 * Goal G4: "one dashboard replaces nine separate Excel reports for day-to-day
 * reading". Each panel answers the question one of those reports is opened to
 * answer, so the layout follows the questions, not the files.
 *
 * Read top to bottom: the headline numbers and the charts first, then the
 * families - each one opening onto the simulations run on it. A simulation
 * is launched from the one "New simulation" button, for one family, several
 * or all of them; there is no per-row action, and no second way to plan.
 *
 * ONE filter drives the page: the projects and value streams ticked in the
 * top bar narrow the tiles, every chart and the table together (DSH-08).
 * Nothing defaults to a single family - "All" is all of them.
 *
 * Each table row carries a meter rather than only a number, because "29.3"
 * means nothing until the reader remembers this family targets 18-19 and the
 * one below it targets 8-10. The band is what the planner judges against, so
 * the band is drawn.
 *
 * Charting decisions, and why:
 *   - DOH vs the corridor is a LINE with a shaded band, for ONE family at a
 *     time: corridors differ (18-19, 8-10...), so lines cannot share a band.
 *     A rectangle per family in the filter picks which one is drawn.
 *   - Demand vs production are two series on ONE axis - both are pieces per
 *     week, so they share a scale. Never a second y-axis.
 *   - Every chart has a crosshair tooltip, and the DOH table view is one
 *     click away, so a colour-blind or printed reading still works.
 */

import { Fragment, useEffect, useMemo, useState } from 'react'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { api, type Dashboard as DashboardData, type SimulationSummary, type StreamSummary } from '../api/client'
import { describeOptions, when } from '../components/simulation'
import { DohMeter } from '../components/DohMeter'
import { StatusChip } from '../components/StatusChip'
import { MultiSelect } from '../components/MultiSelect'
import { dohScale } from '../components/dohScale'
import { Applied, IconButton, ModeChip } from '../components/icons'

const SERIES_1 = 'var(--series-1)'
const SERIES_2 = 'var(--series-2)'
const GOOD = 'var(--status-good)'
const WARNING = 'var(--status-warning)'
const CRITICAL = 'var(--status-critical)'

interface Props {
  scenario: string
  streams: StreamSummary[]
  canSimulate: boolean
  /* Bumped by the parent whenever simulations or the plan change. */
  refreshKey: number
  onNewSimulation: (preselected: string[], lock?: boolean) => void
  onOpenSimulation: (id: number) => void
  onEditSimulation: (id: number) => void
  onDeleteSimulation: (id: number) => void
  /* A simulation was chosen for a family: every figure must follow. */
  onChanged: () => void
}

const COLUMNS: [string, string][] = [
  ['', ''],
  ['Value stream', 'left'],
  ['Crews', ''],
  ['Corridor', ''],
  ['Avg DOH', ''],
  ['Against the corridor', 'left'],
  ['In band', ''],
  ['Short', ''],
  ['Excess', ''],
  ['OT days', ''],
  ['Simulations', ''],
]

/* A family is judged on its AVERAGE DOH against its own corridor - the
   figure the planner reads on the row. Requiring every one of 47 weeks in
   band would count a family at 18.8 as out, for two weeks of excess
   inherited from the opening stock that no decision can remove. */
const standing = (s: StreamSummary) =>
  s.average_doh < s.min_doh ? 'short' : s.average_doh > s.max_doh ? 'excess' : 'in'

export function Dashboard({
  scenario,
  streams,
  canSimulate,
  refreshKey,
  onNewSimulation,
  onOpenSimulation,
  onEditSimulation,
  onDeleteSimulation,
  onChanged,
}: Props) {
  const [data, setData] = useState<DashboardData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [simulations, setSimulations] = useState<SimulationSummary[]>([])
  /* family -> the simulation it is shown with; null = its plan. */
  const [chosen, setChosen] = useState<Record<string, number | null>>({})
  const [reload, setReload] = useState(0)
  const [open, setOpen] = useState<Set<string>>(new Set())
  /* The two filters; empty = all. */
  const [projects, setProjects] = useState<string[]>([])
  const [families, setFamilies] = useState<string[]>([])

  const allProjects = useMemo(() => [...new Set(streams.map((s) => s.project))].sort(), [streams])
  const inProjects = projects.length ? streams.filter((s) => projects.includes(s.project)) : streams
  const visible = families.length ? inProjects.filter((s) => families.includes(s.id)) : inProjects
  /* What the charts are asked for: nothing when nothing is filtered (all). */
  const filtered = projects.length > 0 || families.length > 0
  const chartKey = filtered ? visible.map((s) => s.id).join('|') : ''

  useEffect(() => {
    api
      .dashboard(scenario, chartKey ? chartKey.split('|') : [])
      .then(setData)
      .catch((e) => setError((e as Error).message))
  }, [scenario, chartKey, refreshKey])

  useEffect(() => {
    api
      .simulations()
      .then((d) => {
        setSimulations(d.simulations)
        setChosen(d.chosen)
      })
      .catch(() => setSimulations([]))
  }, [refreshKey, reload])

  /* The server re-reads every family through its chosen simulation, so
     the table, the tiles, the charts and the reports all move together. */
  const choose = (family: string, id: number | null) => {
    setChosen((prev) => ({ ...prev, [family]: id }))
    void api
      .chooseSimulation(family, id)
      .then(onChanged)
      .catch((e: Error) => {
        setError(e.message)
        setReload((r) => r + 1)
      })
  }

  const byFamily = useMemo(() => {
    const out: Record<string, SimulationSummary[]> = {}
    simulations.forEach((s) => (out[s.value_stream] ??= []).push(s))
    return out
  }, [simulations])

  // A project filter may leave value streams ticked that it now hides.
  const pickProjects = (next: string[]) => {
    setProjects(next)
    if (next.length) setFamilies((f) => f.filter((id) => streams.some((s) => s.id === id && next.includes(s.project))))
  }

  if (error) return <div className="card" style={{ padding: 20 }}>{error}</div>
  if (!data) return <div className="card" style={{ padding: 20 }}>Loading…</div>

  const crews = visible.reduce((sum, s) => sum + s.crews, 0)
  const inBand = visible.filter((s) => standing(s) === 'in').length
  const withExcess = visible.filter((s) => standing(s) === 'excess').length
  const overtime = visible.reduce((sum, s) => sum + s.overtime_days, 0)
  const runs = simulations.filter((r) => visible.some((s) => s.id === r.value_stream)).length
  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {/* The top bar stays in view while scrolling: the filters apply to
          everything below it (DSH-08). */}
      <div className="card page-head">
        <div className="page-title">
          <h1>Dashboard</h1>
          <div className="page-sub">
            cycle {data.cycle} · {data.state}
          </div>
        </div>
        <div className="page-actions">
          <MultiSelect
            label="Projects"
            options={allProjects.map((p) => ({ id: p, label: p }))}
            value={projects}
            onChange={pickProjects}
          />
          <MultiSelect
            label="Value streams"
            options={inProjects.map((s) => ({ id: s.id, label: s.name }))}
            value={families}
            onChange={setFamilies}
          />
          {canSimulate && (
            <button className="primary" onClick={() => onNewSimulation(families)}>
              + New simulation
            </button>
          )}
        </div>
      </div>

      {/* Headline tiles - a number, not a chart, when there is one number. */}
      <div className="tiles">
        <Tile label="Value streams in band" value={inBand} of={visible.length} tone="good" />
        <Tile label="With excess stock" value={withExcess} tone={withExcess ? 'warning' : 'neutral'} />
        <Tile label="Overtime days" value={overtime} tone="neutral" />
        <Tile label="Crews scheduled" value={crews} tone="neutral" />
        <Tile label="Production hours" value={Math.round(data.summary.production_hours)} tone="neutral" />
        <Tile label="Simulations" value={runs} tone="neutral" />
      </div>

      <DohPanel data={data} families={visible} />

      <Panel title="Demand against production" subtitle="pieces per week">
        <ChartFrame>
          <ComposedChart data={mergeSeries(data.panels.demand_vs_capacity.demand, data.panels.demand_vs_capacity.production, 'Demand', 'Production')}>
            <CartesianGrid stroke="var(--border)" vertical={false} />
            <XAxis dataKey="label" {...axis} interval={3} />
            <YAxis {...axis} width={62} tickFormatter={compact} />
            <Tooltip {...tooltip} />
            <Legend {...legend} />
            <Bar dataKey="Demand" fill={SERIES_1} radius={[4, 4, 0, 0]} maxBarSize={14} />
            <Line dataKey="Production" stroke={SERIES_2} strokeWidth={2} dot={false} />
          </ComposedChart>
        </ChartFrame>
        {data.panels.demand_vs_capacity.over_mpw.length > 0 && (
          <p className="small" style={{ margin: '8px 0 0', color: 'var(--text-secondary)' }}>
            <span className="chip warning"><span aria-hidden="true">▲</span>above contracted maximum</span>{' '}
            {data.panels.demand_vs_capacity.over_mpw.length} week(s)
          </p>
        )}
      </Panel>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 12 }}>
        <Panel title="Headcount needed" subtitle="direct and indirect">
          <ChartFrame height={200}>
            <ComposedChart data={data.panels.headcount}>
              <CartesianGrid stroke="var(--border)" vertical={false} />
              <XAxis dataKey="label" {...axis} interval={5} />
              <YAxis {...axis} width={48} />
              <Tooltip {...tooltip} />
              <Bar dataKey="value" name="People" fill={SERIES_1} radius={[4, 4, 0, 0]} maxBarSize={12} />
            </ComposedChart>
          </ChartFrame>
        </Panel>

        <Panel title="Work plan" subtitle="overtime and days not worked">
          <ChartFrame height={200}>
            <ComposedChart data={mergeSeries(data.panels.work_plan.overtime, data.panels.work_plan.days_not_worked, 'Overtime', 'Not worked')}>
              <CartesianGrid stroke="var(--border)" vertical={false} />
              <XAxis dataKey="label" {...axis} interval={5} />
              <YAxis {...axis} width={48} />
              <Tooltip {...tooltip} />
              <Legend {...legend} />
              <Bar dataKey="Overtime" fill={SERIES_2} radius={[4, 4, 0, 0]} maxBarSize={10} />
              <Bar dataKey="Not worked" fill={SERIES_1} radius={[4, 4, 0, 0]} maxBarSize={10} />
            </ComposedChart>
          </ChartFrame>
        </Panel>
      </div>

      {/* The families, each opening onto the simulations run on it. */}
      <section className="card" style={{ padding: 0, overflow: 'auto' }}>
        <div style={{ padding: '14px 16px 10px' }}>
          <h2 style={{ margin: 0 }}>Value streams</h2>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              {COLUMNS.map(([label, cls], i) => (
                <th key={label || `c${i}`} className={cls}>
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((s) => {
              const familyRuns = byFamily[s.id] ?? []
              const expanded = open.has(s.id)
              const st = standing(s)
              return (
                <Fragment key={s.id}>
                  <tr onClick={() => toggle(s.id)} style={{ cursor: 'pointer' }} aria-expanded={expanded}>
                    <td style={{ width: 28, color: 'var(--text-secondary)' }}>{expanded ? '▾' : '▸'}</td>
                    <td className="left" style={{ fontWeight: 600 }}>
                      <span
                        aria-hidden="true"
                        style={{ marginRight: 8, color: st === 'short' ? CRITICAL : st === 'excess' ? WARNING : GOOD }}
                      >
                        {st === 'short' ? '▼' : st === 'excess' ? '▲' : '●'}
                      </span>
                      {s.name}
                      {s.shown_simulation && (
                        <div
                          className="small muted"
                          style={{ fontWeight: 400 }}
                          title="Every figure of this row, the charts and the reports use this simulation"
                        >
                          {s.shown_simulation.name}
                        </div>
                      )}
                    </td>
                    <td className="num">{s.crews}</td>
                    <td className="num muted">
                      {s.min_doh}–{s.max_doh}
                    </td>
                    <td className="num" style={{ fontWeight: 600 }}>
                      {s.average_doh}
                    </td>
                    <td className="left">
                      <DohMeter value={s.average_doh} min={s.min_doh} max={s.max_doh} />
                    </td>
                    <td className="num" style={{ color: GOOD, fontWeight: 600 }}>
                      {s.weeks_in_band}
                    </td>
                    <td className="num" style={{ color: s.weeks_below_min ? CRITICAL : undefined }}>
                      {s.weeks_below_min || '—'}
                    </td>
                    <td className="num" style={{ color: s.weeks_above_max ? WARNING : undefined }}>
                      {s.weeks_above_max || '—'}
                    </td>
                    <td className="num muted">{s.overtime_days || '—'}</td>
                    <td className="num">
                      <span className={`chip ${familyRuns.length ? 'good' : 'neutral'}`}>{familyRuns.length}</span>
                    </td>
                  </tr>
                  {expanded && (
                    <tr>
                      <td colSpan={COLUMNS.length} style={{ padding: 0 }}>
                        <FamilySimulations
                          family={s.id}
                          runs={familyRuns}
                          chosen={chosen[s.id] ?? null}
                          onChoose={(id) => choose(s.id, id)}
                          canSimulate={canSimulate}
                          onOpen={onOpenSimulation}
                          onEdit={onEditSimulation}
                          onDelete={onDeleteSimulation}
                          onNew={() => onNewSimulation([s.id], true)}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </section>
    </div>
  )
}

/* The simulations of one family, under its row. The radio picks the one the
 * family is shown with - that one is "applied", and its status says so at
 * once; ticking another moves the status with it. */
function FamilySimulations({
  family,
  runs,
  chosen,
  onChoose,
  canSimulate,
  onOpen,
  onEdit,
  onDelete,
  onNew,
}: {
  family: string
  runs: SimulationSummary[]
  chosen: number | null
  onChoose: (id: number | null) => void
  canSimulate: boolean
  onOpen: (id: number) => void
  onEdit: (id: number) => void
  onDelete: (id: number) => void
  onNew: () => void
}) {
  return (
    <div className="sim-list">
      <table className="sim-table">
        <thead>
          <tr>
            <th />
            <th>Simulation</th>
            <th>Mode</th>
            <th>Created</th>
            <th>In band</th>
            <th>Excess</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          <tr className={chosen === null ? 'chosen' : ''}>
            <td>
              <input
                type="radio"
                name={`shown-${family}`}
                checked={chosen === null}
                disabled={!canSimulate}
                onChange={() => onChoose(null)}
                aria-label="Use the current plan"
              />
            </td>
            <td colSpan={5} className="muted">Current plan</td>
            <td><Applied on={chosen === null} /></td>
            <td />
          </tr>
          {runs.map((r) => (
            <tr key={r.id} className={chosen === r.id ? 'chosen' : ''}>
              <td>
                <input
                  type="radio"
                  name={`shown-${family}`}
                  checked={chosen === r.id}
                  disabled={!canSimulate}
                  onChange={() => onChoose(r.id)}
                  aria-label={`Apply ${r.name}`}
                />
              </td>
              <td>
                <button className="link" onClick={() => onOpen(r.id)}>{r.name}</button>
                <div className="small muted">{describeOptions(r)}</div>
              </td>
              <td><ModeChip mode={r.mode} /></td>
              <td className="small" title={`by ${r.created_by}`}>{when(r.created_at)}</td>
              <td className="mono">
                <span className="muted">{r.before.weeks_in_band}</span> → <strong>{r.after.weeks_in_band}</strong>
              </td>
              <td className="mono">
                <span className="muted">{r.before.weeks_above_max}</span> → <strong>{r.after.weeks_above_max}</strong>
              </td>
              <td><Applied on={chosen === r.id} /></td>
              <td>
                <div className="row-actions">
                  <IconButton icon="open" title="Open" onClick={() => onOpen(r.id)} />
                  {canSimulate && (
                    <>
                      <IconButton icon="edit" title="Edit" onClick={() => onEdit(r.id)} />
                      <IconButton icon="trash" title="Delete" danger onClick={() => onDelete(r.id)} />
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {canSimulate && (
        <div>
          <button className="small" onClick={onNew}>
            + New simulation on {family}
          </button>
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------ DOH panel */

/* One family's DOH against its corridor, always - a line and a band are
 * readable, a stack of 13 families is not. Above the chart, one rectangle
 * per family in the filter: click one and the chart shows it. With a single
 * family in the filter there is a single rectangle, already selected. */
function DohPanel({ data, families }: { data: DashboardData; families: StreamSummary[] }) {
  const [asTable, setAsTable] = useState(false)
  const [picked, setPicked] = useState<string>('')
  const names = Object.keys(data.panels.doh)
  const current = names.includes(picked) ? picked : names[0]
  const rows = data.panels.doh[current] ?? []
  const band = rows[0] ? { low: rows[0].min_doh, high: rows[0].max_doh } : { low: 0, high: 0 }

  /* The horizon-edge weeks are drawn apart, dashed and grey: their cover
     runs past the data, so their drop is not a shortage. */
  const merged = useMemo(
    () =>
      rows.map((r, i) => ({
        label: r.label,
        DOH: r.edge ? null : r.value,
        'Not judged': r.edge || rows[i + 1]?.edge ? r.value : null,
      })),
    [rows],
  )
  const scale = dohScale(rows.map((r) => r.value), band.low, band.high)

  return (
    <Panel
      title={`Days on hand — ${current ?? ''}`}
      subtitle={`corridor ${band.low}–${band.high} days`}
      action={
        <IconButton
          icon={asTable ? 'chart' : 'table'}
          title={asTable ? 'Show chart' : 'Show table'}
          onClick={() => setAsTable(!asTable)}
        />
      }
    >
      <div className="family-picker" role="tablist" aria-label="Value stream shown">
        {names.map((n) => {
          const s = families.find((f) => f.id === n)
          const st = s ? standing(s) : 'in'
          return (
            <button
              key={n}
              type="button"
              role="tab"
              aria-selected={n === current}
              title={s ? `${s.name} · average DOH ${s.average_doh}` : n}
              className={`family-pick ${n === current ? 'on' : ''}`}
              onClick={() => setPicked(n)}
            >
              <span className="family-pick-name">{s?.name ?? n}</span>
              <span className="family-pick-doh mono">
                <span aria-hidden="true" style={{ color: st === 'short' ? CRITICAL : st === 'excess' ? WARNING : GOOD }}>
                  {st === 'short' ? '▼' : st === 'excess' ? '▲' : '●'}
                </span>
                {s?.average_doh}
              </span>
            </button>
          )
        })}
      </div>

      {asTable ? (
        <div style={{ maxHeight: 280, overflow: 'auto' }}>
          <table className="small mono" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Week', 'DOH', 'Status'].map((h) => (
                  <th key={h} style={{ textAlign: 'left', padding: '5px 8px', position: 'sticky', top: 0, background: 'var(--surface-3)', fontSize: 11 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td style={tcell}>{r.label}</td>
                  <td style={tcell}>{r.value.toFixed(1)}</td>
                  <td style={tcell}>
                    {r.edge ? <span className="chip neutral">not judged</span> : <StatusChip status={r.status} label={r.status.toLowerCase()} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <ChartFrame height={260}>
          <ComposedChart data={merged}>
            <CartesianGrid stroke="var(--border)" vertical={false} />
            {/* The corridor itself - the decision rule, drawn. */}
            <ReferenceArea y1={band.low} y2={band.high} fill={GOOD} fillOpacity={0.12} stroke={GOOD} strokeOpacity={0.35} />
            <XAxis dataKey="label" {...axis} interval={3} />
            <YAxis {...axis} width={44} domain={scale.domain} ticks={scale.ticks} allowDataOverflow />
            <Tooltip {...tooltip} />
            <Line dataKey="DOH" stroke={SERIES_1} strokeWidth={2} dot={false} connectNulls={false} activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--surface-1)' }} />
            <Line dataKey="Not judged" stroke="var(--text-muted)" strokeDasharray="4 3" strokeWidth={1.5} dot={false} />
          </ComposedChart>
        </ChartFrame>
      )}
    </Panel>
  )
}

/* ---------------------------------------------------------------- bits */

const tcell: React.CSSProperties = { padding: '4px 8px', borderBottom: '1px solid var(--border)' }

const axis = {
  stroke: 'var(--border-strong)',
  tick: { fill: 'var(--text-secondary)', fontSize: 11 },
  tickLine: false,
} as const

const tooltip = {
  contentStyle: {
    background: 'var(--surface-1)',
    border: '1px solid var(--border-strong)',
    borderRadius: 8,
    fontSize: 12,
    color: 'var(--text-primary)',
  },
  labelStyle: { color: 'var(--text-secondary)', fontSize: 11 },
} as const

const legend = { wrapperStyle: { fontSize: 12, color: 'var(--text-secondary)' }, iconType: 'plainline' } as const

function ChartFrame({ children, height = 240 }: { children: React.ReactElement; height?: number }) {
  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>{children}</ResponsiveContainer>
    </div>
  )
}

function Panel({
  title,
  subtitle,
  action,
  children,
}: {
  title: string
  subtitle?: string
  action?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
        <h2>{title}</h2>
        {subtitle && <span className="small muted">{subtitle}</span>}
        {action && <div style={{ marginLeft: 'auto' }}>{action}</div>}
      </div>
      {children}
    </section>
  )
}

function Tile({
  label,
  value,
  of,
  tone,
}: {
  label: string
  value: number
  of?: number
  tone: 'good' | 'warning' | 'critical' | 'neutral'
}) {
  return (
    <div className={`tile ${tone === 'neutral' ? '' : tone}`}>
      <div className="tile-label">{label}</div>
      <div className="tile-value">
        {value.toLocaleString()}
        {of !== undefined && <span className="tile-of"> / {of}</span>}
      </div>
    </div>
  )
}

/* Merge two weekly series positionally.
 *
 * Keyed by INDEX, never by label: a 52-week horizon crosses a year boundary,
 * so "W03" occurs twice and keying by it would collapse the first week of the
 * cycle into its last. Both series come from the same bucket order, so index
 * is the reliable join.
 */
function mergeSeries(
  a: { label: string; value: number }[],
  b: { label: string; value: number }[],
  nameA: string,
  nameB: string,
) {
  const length = Math.max(a.length, b.length)
  return Array.from({ length }, (_, i) => ({
    label: a[i]?.label ?? b[i]?.label ?? '',
    [nameA]: a[i]?.value ?? 0,
    [nameB]: b[i]?.value ?? 0,
  }))
}

function compact(value: number): string {
  const abs = Math.abs(value)
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `${Math.round(value / 1_000)}k`
  return String(Math.round(value))
}
